import {groupBy} from 'lodash';

import {featureDisplayName} from 'app/components/Omnisearch';
import {ApiFeature} from 'app/modules/Remote/Feature';
import {ApiOrganizationAreaUnit} from 'app/modules/Remote/Organization';
import {
  AgentFeatureResult,
  ApiAgentChildRun,
  ApiAgentNote,
  ERROR_KIND_NO_IMAGERY,
  SortDirection,
  SortField,
} from 'app/pages/Agent/types';
import {NOTE_TYPE_USER} from 'app/utils/constants';
import {alphanumericSort} from 'app/utils/featureUtils';
import {calculateAreaInUnit, makeAreaDisplayString} from 'app/utils/geoJsonUtils';

export const isNoteSaved = (note: ApiAgentNote) => note.noteType == NOTE_TYPE_USER;
export const isNoteDismissed = (note: ApiAgentNote) => note.isArchived;

// Saved and dismissed are independent flags, so a note can count towards both.
export const countNoteReviewStates = (notes: ApiAgentNote[]) => {
  let savedCount = 0;
  let dismissedCount = 0;
  let newCount = 0;
  for (const note of notes) {
    const saved = isNoteSaved(note);
    const dismissed = isNoteDismissed(note);
    if (saved) savedCount++;
    if (dismissed) dismissedCount++;
    if (!saved && !dismissed) newCount++;
  }
  return {savedCount, dismissedCount, newCount};
};

// Order for the per-property breakdown: properties with findings first (most first), then errors,
// then missing imagery, then clean ones, with unsettled runs last; alphabetical within a group.
export const sortFeatureResults = (results: AgentFeatureResult[]): AgentFeatureResult[] => {
  const groupRank = (result: AgentFeatureResult) => {
    if (result.status === 'completed') {
      return result.findingsCount > 0 ? 0 : 3;
    }
    if (result.status === 'failed') {
      return result.errorKind === ERROR_KIND_NO_IMAGERY ? 2 : 1;
    }
    return 4;
  };
  return [...results].sort((a, b) => {
    const rank = groupRank(a) - groupRank(b);
    if (rank !== 0) return rank;
    if (b.findingsCount !== a.findingsCount) return b.findingsCount - a.findingsCount;
    return alphanumericSort(a.featureName, b.featureName);
  });
};

// The run detail endpoint returns the run's child runs and its notes rather than a per-feature
// rollup, so the breakdown is assembled here from both.
export const buildFeatureResults = (
  agentRuns: ApiAgentChildRun[],
  notes: ApiAgentNote[],
  features: ApiFeature[],
  areaUnit: ApiOrganizationAreaUnit
): AgentFeatureResult[] => {
  const featuresById = new Map(features.map((feature) => [Number(feature.id), feature]));
  const notesByFeatureId = groupBy(notes, (note) => Number(note.featureId));
  return agentRuns.flatMap((agentRun) => {
    // The endpoint omits archived features but keeps their child runs, so a feature archived
    // after the run would otherwise show up as an unnamed ghost row.
    const feature = featuresById.get(Number(agentRun.featureId));
    if (!feature) return [];
    const featureNotes = notesByFeatureId[agentRun.featureId] ?? [];
    return [
      {
        featureId: agentRun.featureId,
        featureName: featureDisplayName(feature),
        propertyArea: makeAreaDisplayString(feature.geometry ?? null, areaUnit, true),
        findingsCount: featureNotes.length,
        ...countNoteReviewStates(featureNotes),
        status: agentRun.status,
        errorKind: agentRun.properties?.error_kind ?? null,
      },
    ];
  });
};

export const sortAndFilterNotes = (
  notes: ApiAgentNote[],
  features: ApiFeature[],
  filters,
  areaUnit
) => {
  if (!notes || !features) return [];
  let filtered = notes;

  const searchQuery = (filters.searchParams[1] ?? '').trim();
  if (filters.searchParams[0] === 'featureName' && searchQuery) {
    const q = searchQuery.toLowerCase();
    filtered = filtered.filter((note) => {
      const feature = features.find((f) => Number(f.id) === Number(note.featureId));
      if (!feature) return false;
      const featureName = featureDisplayName(feature);
      return featureName.toLowerCase().includes(q);
    });
  }

  if (filters.searchParams[0] === 'observationText' && searchQuery) {
    const q = searchQuery.toLowerCase();
    filtered = filtered.filter((note) => (note.text || '').toLowerCase().includes(q));
  }

  if (filters.status !== 'all') {
    filtered = filtered.filter((note) => {
      const saved = isNoteSaved(note);
      const archived = isNoteDismissed(note);
      if (filters.status === 'saved') return saved && !archived;
      if (filters.status === 'archived') return archived;
      if (filters.status === 'new') return !saved && !archived;
      return false;
    });
  }

  const [sortField, sortDirection] = filters.sortParams as [SortField, SortDirection];
  if (sortDirection) {
    filtered = [...filtered].sort((a, b) => {
      // derive values for each sort field
      const featureA = features.find((f) => f.id === a.featureId);
      const featureB = features.find((f) => f.id === b.featureId);
      const getFieldValue = (note: ApiAgentNote, featureName: string | null): string => {
        switch (sortField) {
          case 'featureName':
            return featureName || '';
          case 'observationText':
            return note.text || '';
          case 'status': {
            const saved = isNoteSaved(note);
            const archived = isNoteDismissed(note);
            return archived ? 'Archived' : saved ? 'Saved' : 'New';
          }
          case 'propertyArea': {
            const feature = features.find((f) => f.id === note.featureId);
            return feature?.geometry
              ? calculateAreaInUnit(feature.geometry, areaUnit)?.toFixed(2) || 'N/A'
              : 'N/A';
          }
          case 'observationGeometryArea': {
            return note.geometry
              ? calculateAreaInUnit(note.geometry, areaUnit)?.toFixed(2) || 'N/A'
              : 'N/A';
          }
          default:
            return '';
        }
      };
      const nameA = featureA ? featureDisplayName(featureA) : null;
      const nameB = featureB ? featureDisplayName(featureB) : null;
      const aVal = getFieldValue(a, nameA);
      const bVal = getFieldValue(b, nameB);

      return alphanumericSort(aVal, bVal, sortDirection);
    });
  }

  return filtered;
};
